export const NOTIFICATION_DELIVERY_TYPE_SMS = 'sms';
export const NOTIFICATION_DELIVERY_TYPE_EMAIL = 'email';
export const NOTIFICATION_DELIVERY_TYPE_PUSH = 'push';

export const NOTIFICATION_DELIVERY_TYPES = [
  NOTIFICATION_DELIVERY_TYPE_SMS,
  NOTIFICATION_DELIVERY_TYPE_EMAIL,
  NOTIFICATION_DELIVERY_TYPE_PUSH,
];

export const NOTIFICATION_LOG_STATE__INITIAL = 'INITIAL';
export const NOTIFICATION_LOG_STATE__ABORTED = 'ABORTED';
export const NOTIFICATION_LOG_STATE__SENDING = 'ATTEMPTED';
export const NOTIFICATION_LOG_STATE__ACCEPTED = 'ACCEPTED';
export const NOTIFICATION_LOG_STATE__ENQUEUED = 'ENQUEUED';
export const NOTIFICATION_LOG_STATE__SENT = 'SENT';
export const NOTIFICATION_LOG_STATE__FAILED = 'FAILED';
export const NOTIFICATION_LOG_STATE__DELIVERED = 'DELIVERED';
export const NOTIFICATION_LOG_STATE__RECEIVED = 'RECEIVED';

export const NOTIFICATION_LOG_STATE__OPENED = 'OPENED';
export const NOTIFICATION_LOG_STATE__CLICKED = 'CLICKED';

export const NOTIFICATION_LOG_STATES = [
  NOTIFICATION_LOG_STATE__INITIAL,
  NOTIFICATION_LOG_STATE__ABORTED,
  NOTIFICATION_LOG_STATE__SENDING,
  NOTIFICATION_LOG_STATE__ACCEPTED,
  NOTIFICATION_LOG_STATE__ENQUEUED,
  NOTIFICATION_LOG_STATE__SENT,
  NOTIFICATION_LOG_STATE__FAILED,
  NOTIFICATION_LOG_STATE__DELIVERED,
  NOTIFICATION_LOG_STATE__RECEIVED,
  NOTIFICATION_LOG_STATE__OPENED,
  NOTIFICATION_LOG_STATE__CLICKED,
];

// CREATE - I don't think this is used anywhere, we can probably delete it.
export const NOTIFICATION_EVENT_TYPE__CREATE = 'CREATE';
// ATTEMPT - the process of sending a message was started.
export const NOTIFICATION_EVENT_TYPE__ATTEMPT = 'ATTEMPT';
// CANCEL - the process was canceled due to an error at an early stage, e.g. network connectivity issues.
export const NOTIFICATION_EVENT_TYPE__CANCEL = 'CANCEL';
// ACCEPTED - messaging-apigateway took the request but the vendor has not answered yet. The
//            gateway replaces it with SEND or FAILED once the vendor answers, so a log left at
//            ACCEPTED is one whose send never finished on the gateway side.
export const NOTIFICATION_EVENT_TYPE__ACCEPT = 'ACCEPTED';
// ENQUEUE - the message was accepted by corresponding (sms or email) cloud based microservice.
export const NOTIFICATION_EVENT_TYPE__ENQUEUE = 'ENQUEUE';
// SEND - (deprecated) it's equivalent to ENQUEUE
export const NOTIFICATION_EVENT_TYPE__SEND = 'SEND';
// DELIVER - a delivery confirmation was obtained from provider.
export const NOTIFICATION_EVENT_TYPE__DELIVER = 'DELIVER';
// RECEIVE - the receiver clicked on the link in the message.
export const NOTIFICATION_EVENT_TYPE__RECEIVE = 'RECEIVE';
// FAILED - a delivery failure was confirmed by provider.
export const NOTIFICATION_EVENT_TYPE__DELIVERY_FAILURE = 'FAILED';

export const NOTIFICATION_EVENT_TYPE__OPEN = 'OPEN';
export const NOTIFICATION_EVENT_TYPE__CLICK = 'CLICK';

export const NOTIFICATION_EVENT_TYPES = [
  NOTIFICATION_EVENT_TYPE__CREATE,
  NOTIFICATION_EVENT_TYPE__CANCEL,
  NOTIFICATION_EVENT_TYPE__ATTEMPT,
  NOTIFICATION_EVENT_TYPE__ACCEPT,
  NOTIFICATION_EVENT_TYPE__ENQUEUE,
  NOTIFICATION_EVENT_TYPE__SEND,
  NOTIFICATION_EVENT_TYPE__DELIVER,
  NOTIFICATION_EVENT_TYPE__RECEIVE,
  NOTIFICATION_EVENT_TYPE__DELIVERY_FAILURE,
  NOTIFICATION_EVENT_TYPE__OPEN,
  NOTIFICATION_EVENT_TYPE__CLICK,
];

// How far along the delivery lifecycle each state sits. Only used to compare
// two states, so the numbers carry no meaning beyond their order.
//
// The ordering answers one question: if two events disagree, which one is
// telling us more about where the message got to? So a terminal answer beats
// an in-progress one, and evidence the message reached someone beats evidence
// it was handed over.
//
// FAILED sits below DELIVERED because a delivery confirmation is final: once
// the carrier has confirmed, a failure report can only belong to an earlier,
// abandoned message id. It sits above ENQUEUED so that a real failure is never
// hidden behind a "still queued" status.
//
// ABORTED sits above SENDING, following the documented order in "NotificationLogs
// Collection and Status's meaning" (Confluence, Service Desk space). A cancel is
// a verdict and an attempt is not.
//
// In practice the two rarely meet. ABORTED comes from
// NotificationIntents.sendNotifications, which emits CANCEL when the log row
// was written but the send never started -- login-URL generation threw, or
// NotifierMethod could not reach AMQP. Those logs hold no ATTEMPT at all. The
// CANCEL that sendMessage raises from the catch around the provider call
// always follows its own ATTEMPT, so that one resolves to FAILED instead.
//
// So ABORTED means the message never left this server. That is worth showing,
// and hiding it was the bug: the code emitting those cancels says in its own
// comments that it exists to stop the log being orphaned, yet evaluate() and
// RemindersList then filtered the log out anyway and the activity appeared
// with no notification against it. Both now keep aborted logs, and the
// provider UI already had a red badge and an "aborted" label waiting.
//
// Surfacing them is not simply additive, though. evaluate() stores one
// notification per activity and picks it by timestamp, so it also had to learn
// that an aborted log may not displace one that got somewhere -- a second
// reminder that fails to enqueue is stamped later than the first that
// succeeded. Without that rule the milestone chip would have gone red while a
// real delivery vanished from the record.
//
// Note that these two ranks are never actually compared. The model reads
// ABORTED only from a cancel with no ATTEMPT anywhere in the log, so a log
// resolving to ABORTED cannot also hold an ATTEMPT. The order above is kept
// because it is the documented one, not because anything depends on it.
//
// ACCEPTED sits above SENDING and ABORTED and below ENQUEUED. It is reported by
// messaging-apigateway when it takes the request, so the message has left this
// server (more than SENDING says, and the opposite of ABORTED). ENQUEUED comes
// from the patient worker once the gateway has answered, which it does only
// after the vendor has the message, so ENQUEUED says more. Usually the gateway
// replaces ACCEPTED with SEND or FAILED before the relay reads it, and the
// provider never sees it; a log that stays at ACCEPTED is a send that stalled
// inside the gateway.
//
// RECEIVED sits at the top because it is the only one of these the patient
// causes: they followed the link and arrived. OPENED and CLICKED come from
// mail-provider tracking, which fires earlier and is a weaker signal, so
// neither may displace it.
//
// That top rank rests on RECEIVE being raised only by the link shortener, so
// a provider status must never be mapped to it. Optus was, until this branch:
// 'submitted' and 'received' both became RECEIVE, which means a message the
// carrier later killed outranks its own failure report and still reads as
// answered.
//
// Remapping the producer only stops new logs being written that way, so any
// Optus log already stored would still hold a RECEIVE that means "accepted by
// the carrier" -- and this ranking would make those worse, not better, since
// RECEIVED at the top cannot be beaten by the failure that follows it. No
// migration is needed only because no client is on Optus yet and there are no
// such logs; every Optus log will be written under the corrected mapping.
// Worth knowing before switching a client to Optus while any old log exists,
// and worth knowing that the ranking cannot rescue that case: RECEIVED has to
// outrank FAILED for every provider that reports it honestly.
//
// This lives here rather than next to the reducer because the analytics export
// ranks the same lifecycle and the two must not drift apart. TECH-2749.
export const NOTIFICATION_LOG_STATE_PROGRESS = {
  [NOTIFICATION_LOG_STATE__INITIAL]: 0,
  [NOTIFICATION_LOG_STATE__SENDING]: 1,
  [NOTIFICATION_LOG_STATE__ABORTED]: 2,
  [NOTIFICATION_LOG_STATE__ACCEPTED]: 3,
  [NOTIFICATION_LOG_STATE__ENQUEUED]: 4,
  [NOTIFICATION_LOG_STATE__SENT]: 5,
  [NOTIFICATION_LOG_STATE__FAILED]: 6,
  [NOTIFICATION_LOG_STATE__DELIVERED]: 7,
  [NOTIFICATION_LOG_STATE__OPENED]: 8,
  [NOTIFICATION_LOG_STATE__CLICKED]: 9,
  [NOTIFICATION_LOG_STATE__RECEIVED]: 10,
};

// The state each event type reports. CANCEL is deliberately absent: it means
// FAILED when a send was already under way and ABORTED otherwise, so it cannot
// be expressed as a fixed mapping. Every consumer handles that one case itself.
export const NOTIFICATION_EVENT_TYPE_STATE = {
  [NOTIFICATION_EVENT_TYPE__CREATE]: NOTIFICATION_LOG_STATE__INITIAL,
  [NOTIFICATION_EVENT_TYPE__ATTEMPT]: NOTIFICATION_LOG_STATE__SENDING,
  [NOTIFICATION_EVENT_TYPE__ACCEPT]: NOTIFICATION_LOG_STATE__ACCEPTED,
  [NOTIFICATION_EVENT_TYPE__ENQUEUE]: NOTIFICATION_LOG_STATE__ENQUEUED,
  [NOTIFICATION_EVENT_TYPE__SEND]: NOTIFICATION_LOG_STATE__SENT,
  [NOTIFICATION_EVENT_TYPE__DELIVERY_FAILURE]: NOTIFICATION_LOG_STATE__FAILED,
  [NOTIFICATION_EVENT_TYPE__DELIVER]: NOTIFICATION_LOG_STATE__DELIVERED,
  [NOTIFICATION_EVENT_TYPE__RECEIVE]: NOTIFICATION_LOG_STATE__RECEIVED,
  [NOTIFICATION_EVENT_TYPE__OPEN]: NOTIFICATION_LOG_STATE__OPENED,
  [NOTIFICATION_EVENT_TYPE__CLICK]: NOTIFICATION_LOG_STATE__CLICKED,
};
